/**
 * Shared General talent purchase / dice derivation (pure domain).
 * Independent point pool from class (Vanguard) talents.
 */

import {
  GENERAL_DICE_BASELINES,
  GENERAL_TALENT_GATES,
  GENERAL_TALENT_NODES,
  getGeneralTalentNode,
  listGeneralTalentNodes,
  type GeneralBranchId,
  type GeneralTalentEffectKind,
  type GeneralTalentNodeDef,
} from '../data/generalTalents';
import type { GeneralProgress, PermanentSave } from '../persistence/schema';
import {
  TalentPurchaseError,
  type TalentPurchasePreview,
  type TalentRankMap,
} from './talents';

const NODE_ID_SET = new Set(GENERAL_TALENT_NODES.map((n) => n.id));

export function emptyGeneralTalentRanks(): TalentRankMap {
  return {};
}

export function normalizeGeneralTalentRanks(raw: unknown): TalentRankMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return emptyGeneralTalentRanks();
  }
  const result: TalentRankMap = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!NODE_ID_SET.has(id)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const node = getGeneralTalentNode(id);
    const ranks = Math.max(0, Math.min(node.maxRanks, Math.floor(value)));
    if (ranks > 0) result[id] = ranks;
  }
  return result;
}

export function generalRanksOf(ranks: TalentRankMap, nodeId: string): number {
  return ranks[nodeId] ?? 0;
}

export function totalGeneralSpentFromRanks(ranks: TalentRankMap): number {
  let total = 0;
  for (const [id, value] of Object.entries(ranks)) {
    if (!NODE_ID_SET.has(id)) continue;
    total += value;
  }
  return total;
}

export function unspentGeneralPoints(progress: GeneralProgress): number {
  return Math.max(0, progress.earnedPoints - progress.spentPoints);
}

export function syncGeneralPointsFromLevel(
  progress: GeneralProgress,
): GeneralProgress {
  const earnedFromLevels = Math.min(
    GENERAL_TALENT_GATES.maxLevelPoints,
    Math.max(0, progress.level - 1),
  );
  // Milestone points stay reserved (0 granted) until schedule is designed.
  const earnedPoints = Math.max(progress.earnedPoints, earnedFromLevels);
  const ranks = normalizeGeneralTalentRanks(progress.talentRanks);
  const spentPoints = totalGeneralSpentFromRanks(ranks);
  return {
    ...progress,
    earnedPoints: Math.min(
      GENERAL_TALENT_GATES.maxTotalGeneralPoints,
      earnedPoints,
    ),
    spentPoints,
    reservedMilestonePoints: 0,
    talentRanks: ranks,
  };
}

export function accumulateGeneralEffectTotals(
  ranks: TalentRankMap,
): Record<GeneralTalentEffectKind, number> {
  const totals = {
    attack_pct: 0,
    crit_damage_pct: 0,
    crit_chance_pp: 0,
    counterattack_chance_pp: 0,
    max_hp_pct: 0,
    armour_pct: 0,
    block_chance_pp: 0,
    dodge_chance_pp: 0,
    additional_dice_slots: 0,
    special_die_slot: 0,
    targeted_rerolls: 0,
    full_rerolls: 0,
  } satisfies Record<GeneralTalentEffectKind, number>;

  for (const node of GENERAL_TALENT_NODES) {
    const r = generalRanksOf(ranks, node.id);
    if (r <= 0) continue;
    totals[node.effect.kind] += node.effect.perRank * r;
  }
  return totals;
}

export interface DiceTalentBonuses {
  /** 1–6 standard unlocked slots derived from talents. */
  unlockedDiceSlots: number;
  /** Talent unlock only — equipment Special Die effects deferred. */
  specialDieSlotUnlocked: boolean;
  targetedRerollsPerTurn: number;
  fullRerollsPerTurn: number;
  additionalDiceSlotRanks: number;
  targetedRerollRanks: number;
  fullRerollRanks: number;
}

export function deriveDiceBonusesFromGeneralRanks(
  ranks: TalentRankMap,
): DiceTalentBonuses {
  const t = accumulateGeneralEffectTotals(ranks);
  const slots = Math.min(
    GENERAL_DICE_BASELINES.maxStandardDiceSlots,
    GENERAL_DICE_BASELINES.startingStandardDiceSlots + t.additional_dice_slots,
  );
  return {
    unlockedDiceSlots: slots,
    specialDieSlotUnlocked: t.special_die_slot > 0,
    targetedRerollsPerTurn:
      GENERAL_DICE_BASELINES.baseTargetedRerolls + t.targeted_rerolls,
    fullRerollsPerTurn: GENERAL_DICE_BASELINES.baseFullRerolls + t.full_rerolls,
    additionalDiceSlotRanks: t.additional_dice_slots,
    targetedRerollRanks: t.targeted_rerolls,
    fullRerollRanks: t.full_rerolls,
  };
}

export function previewGeneralTalentPurchase(
  progress: GeneralProgress,
  nodeId: string,
  ranksRequested = 1,
): TalentPurchasePreview {
  const unspentBefore = unspentGeneralPoints(progress);
  const unknown = {
    nodeId,
    currentRanks: 0,
    maxRanks: 0,
    ranksRequested,
    ranksAffordable: 0,
    cost: 0,
    unspentBefore,
    unspentAfter: unspentBefore,
    tierUnlocked: true,
    branchOpen: true,
    canPurchase: false,
    rejectReason: 'unknown_node' as const,
  };

  let node: GeneralTalentNodeDef;
  try {
    node = getGeneralTalentNode(nodeId);
  } catch {
    return unknown;
  }

  const currentRanks = generalRanksOf(progress.talentRanks, nodeId);
  if (ranksRequested <= 0) {
    return {
      nodeId,
      currentRanks,
      maxRanks: node.maxRanks,
      ranksRequested,
      ranksAffordable: 0,
      cost: 0,
      unspentBefore,
      unspentAfter: unspentBefore,
      tierUnlocked: true,
      branchOpen: true,
      canPurchase: false,
      rejectReason: 'invalid_ranks',
    };
  }
  if (currentRanks >= node.maxRanks) {
    return {
      nodeId,
      currentRanks,
      maxRanks: node.maxRanks,
      ranksRequested,
      ranksAffordable: 0,
      cost: 0,
      unspentBefore,
      unspentAfter: unspentBefore,
      tierUnlocked: true,
      branchOpen: true,
      canPurchase: false,
      rejectReason: 'already_maxed',
    };
  }

  const room = node.maxRanks - currentRanks;
  const ranksAffordable = Math.min(room, ranksRequested, unspentBefore);
  const cost = ranksAffordable;
  const canPurchase = ranksAffordable > 0 && ranksAffordable === ranksRequested;

  return {
    nodeId,
    currentRanks,
    maxRanks: node.maxRanks,
    ranksRequested,
    ranksAffordable,
    cost,
    unspentBefore,
    unspentAfter: unspentBefore - cost,
    tierUnlocked: true,
    branchOpen: true,
    canPurchase,
    rejectReason: canPurchase
      ? null
      : ranksAffordable === 0
        ? 'insufficient_points'
        : 'insufficient_points',
  };
}

export function purchaseGeneralTalentRanks(
  progress: GeneralProgress,
  nodeId: string,
  ranksRequested = 1,
): GeneralProgress {
  const preview = previewGeneralTalentPurchase(
    progress,
    nodeId,
    ranksRequested,
  );
  if (!preview.canPurchase) {
    const reason = (preview.rejectReason ??
      'insufficient_points') as import('./talents').TalentPurchaseRejectReason;
    throw new TalentPurchaseError(
      reason,
      `Cannot purchase ${ranksRequested} rank(s) of ${nodeId}: ${reason}`,
    );
  }
  const nextRanks = {
    ...progress.talentRanks,
    [nodeId]: preview.currentRanks + preview.cost,
  };
  const spentPoints = totalGeneralSpentFromRanks(nextRanks);
  return {
    ...progress,
    talentRanks: nextRanks,
    spentPoints,
  };
}

export function purchaseGeneralTalentRanksUpTo(
  progress: GeneralProgress,
  nodeId: string,
  targetRanks: number,
): GeneralProgress {
  const node = getGeneralTalentNode(nodeId);
  const current = generalRanksOf(progress.talentRanks, nodeId);
  const want = Math.min(node.maxRanks, Math.max(current, Math.floor(targetRanks)));
  const delta = want - current;
  if (delta <= 0) return progress;
  return purchaseGeneralTalentRanks(progress, nodeId, delta);
}

export function applyGeneralTalentPurchaseToPermanent(
  permanent: PermanentSave,
  nodeId: string,
  ranksRequested = 1,
): PermanentSave {
  const general = purchaseGeneralTalentRanks(
    permanent.general,
    nodeId,
    ranksRequested,
  );
  const dice = deriveDiceBonusesFromGeneralRanks(general.talentRanks);
  return {
    ...permanent,
    general,
    unlockedDiceSlots: dice.unlockedDiceSlots,
    specialDieSlotUnlocked: dice.specialDieSlotUnlocked,
  };
}

export function respecGeneralTalents(progress: GeneralProgress): GeneralProgress {
  return {
    ...progress,
    talentRanks: {},
    spentPoints: 0,
  };
}

export function generalBranchPointsSpent(
  ranks: TalentRankMap,
  branch: GeneralBranchId,
): number {
  let total = 0;
  for (const node of listGeneralTalentNodes(branch)) {
    total += generalRanksOf(ranks, node.id);
  }
  return total;
}

export type GeneralNodeVisualState =
  | 'locked'
  | 'available'
  | 'purchased'
  | 'maxed';

export function visualStateForGeneralNode(
  progress: GeneralProgress,
  nodeId: string,
): GeneralNodeVisualState {
  const node = getGeneralTalentNode(nodeId);
  const current = generalRanksOf(progress.talentRanks, nodeId);
  if (current >= node.maxRanks) return 'maxed';
  if (current > 0) return 'purchased';
  if (unspentGeneralPoints(progress) > 0) return 'available';
  return 'locked';
}
